import React, { useRef } from 'react';
import { useInteractOutside, useKeyboard } from 'react-aria';
import type { InteractiveElementChipDescriptor } from './elementChipsTypes';

type ElementChipInteractionContainerProps = {
    chip: InteractiveElementChipDescriptor;
    children: React.ReactNode;
};

/**
 * Provides local click and keyboard behaviour for one rendered element chip.
 */
const ElementChipInteractionContainer: React.FC<ElementChipInteractionContainerProps> = ({ chip, children }) => {
    const wrapperRef = useRef<HTMLDivElement | null>(null);

    const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
        if (chip.interaction.isSelected) return;
        if (!chip.interaction.canActivate) return;

        if (chip.activationMode === 'immediate') {
            event.stopPropagation();
            chip.interaction.onPressImmediate?.();
            return;
        }

        chip.interaction.onSelect?.();
        wrapperRef.current?.focus();
    };

    useInteractOutside({
        ref: wrapperRef,
        isDisabled: !chip.interaction.isSelected,
        onInteractOutside: (event) => {
            const target = event.target as Node | null;
            const elementTarget = target instanceof Element ? target : target?.parentElement;
            if (!elementTarget?.closest('.canvas-section')) return;
            if (elementTarget.closest('.RomaPopover')) return;

            chip.interaction.onDeselect?.();
        },
    });

    const { keyboardProps } = useKeyboard({
        onKeyDown: (event) => {
            if (!chip.interaction.isSelected) {
                event.continuePropagation();
                return;
            }

            if (event.key === 'Escape') {
                event.preventDefault();
                chip.interaction.onDeselect?.();
                return;
            }

            if ((event.key === 'Delete' || event.key === 'Backspace') && chip.interaction.onDeleteKeyDown) {
                chip.interaction.onDeleteKeyDown(event);
                return;
            }

            if ((event.key === 'Enter' || event.key === 'F2') && chip.interaction.onEditingChange) {
                event.preventDefault();
                chip.interaction.onEditingChange(!chip.interaction.isEditing);
                return;
            }

            event.continuePropagation();
        },
    });

    return (
        <div
            ref={wrapperRef}
            role="button"
            tabIndex={chip.interaction.canActivate ? 0 : -1}
            onClick={handleClick}
            onDoubleClick={(event) => event.stopPropagation()}
            className="outline-none cursor-pointer"
            {...keyboardProps}
        >
            {children}
        </div>
    );
};

export default ElementChipInteractionContainer;
